import axios from 'axios';
import mainCaller, { getMainUrl, getToken } from './mainCaller';

// 전자계약서(계약서 템플릿) 관리 API
// 모든 호출은 Bearer 토큰 필요. PDF 는 백엔드에서 자동 생성.

const BASE = 'admin/api/v1/contract';

// 1. 계약서 목록 조회
export async function getContractTemplates(token, query) {
  const url = query ? `${BASE}/templates?${query}` : `${BASE}/templates`;
  return await mainCaller(url, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 2. 계약서 상세 조회
export async function getContractTemplate(id, token) {
  return await mainCaller(`${BASE}/templates/${id}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 3. 계약서 생성 (JSON → 백엔드에서 PDF 자동 생성)
export async function createContractTemplate(data, token) {
  return await mainCaller(`${BASE}/templates`, 'POST', data, {
    Authorization: `Bearer ${token}`,
  });
}

// 4. 계약서 수정 (JSON → 백엔드에서 PDF 자동 재생성)
export async function updateContractTemplate(id, data, token) {
  return await mainCaller(`${BASE}/templates/${id}`, 'PUT', data, {
    Authorization: `Bearer ${token}`,
  });
}

// 5. 계약서 삭제
export async function deleteContractTemplate(id, token) {
  return await mainCaller(`${BASE}/templates/${id}`, 'DELETE', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 6. 사용여부 토글 (카테고리당 하나만 active)
export async function toggleContractTemplateActive(id, token) {
  return await mainCaller(`${BASE}/templates/${id}/toggle-active`, 'PATCH', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 7. 저장된 계약서 미리보기 (조항 내용 + 로고/직인 URL)
export async function getContractTemplatePreview(id, token) {
  return await mainCaller(`${BASE}/templates/${id}/preview`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 8. PDF URL 조회
export async function getContractTemplatePdfUrl(id, token) {
  return await mainCaller(`${BASE}/templates/${id}/pdf`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 9. PDF 다운로드 (바이너리). mainCaller 는 JSON 전용이라 blob 응답은 직접 처리한다.
export async function getContractTemplatePdf(id, token) {
  const res = await axios({
    method: 'GET',
    url: `${getMainUrl()}/${BASE}/templates/${id}/pdf/download`,
    headers: { Authorization: `Bearer ${token || getToken()}` },
    responseType: 'blob',
    withCredentials: false,
  });
  return res.data;
}

// 10. 로고/직인 목록 조회 (지점당 1:N — { logos: [], seals: [] })
export async function getContractLogoSeal(branchId, token) {
  return await mainCaller(`${BASE}/logo-seal/${branchId}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 11. 로고/직인 등록 (multipart/form-data)
// - branchId / type('LOGO'|'SEAL') 필수, title / imageFile 선택
export async function createContractLogoSeal({ branchId, type, title, imageFile }, token) {
  const formData = new FormData();
  formData.append('branchId', branchId);
  formData.append('type', type);
  if (title != null) formData.append('title', title);
  if (imageFile) formData.append('imageFile', imageFile);

  // Content-Type 은 axios 가 boundary 와 함께 자동 설정하도록 비워둔다.
  const res = await axios({
    method: 'POST',
    url: `${getMainUrl()}/${BASE}/logo-seal`,
    data: formData,
    headers: { Authorization: `Bearer ${token || getToken()}` },
    withCredentials: false,
  });
  return res.data;
}

// 12. 로고/직인 수정 (multipart/form-data)
// - 새 이미지: imageFile 전송 / 기존 이미지 유지: imageUrl 문자열 전송
export async function updateContractLogoSeal(id, { title, imageFile, imageUrl }, token) {
  const formData = new FormData();
  if (title != null) formData.append('title', title);
  if (imageFile) formData.append('imageFile', imageFile);
  if (imageUrl != null) formData.append('imageUrl', imageUrl);

  const res = await axios({
    method: 'PUT',
    url: `${getMainUrl()}/${BASE}/logo-seal/${id}`,
    data: formData,
    headers: { Authorization: `Bearer ${token || getToken()}` },
    withCredentials: false,
  });
  return res.data;
}

// 13. 로고/직인 삭제
export async function deleteContractLogoSeal(id, token) {
  return await mainCaller(`${BASE}/logo-seal/${id}`, 'DELETE', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 21. 지점+카테고리 활성 템플릿 조회 (환불/중지 버튼 노출·게이팅 판정용)
export async function getActiveContractTemplate(branchId, category, token) {
  return await mainCaller(`${BASE}/templates/active?branchId=${branchId}&category=${category}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// ============================================
// 체결된 계약서 (contracts) API
// ============================================

// 14. 계약 목록 조회
export async function getContracts(token, query) {
  const url = query ? `admin/api/v1/contracts?${query}` : 'admin/api/v1/contracts';
  return await mainCaller(url, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 15. 계약 상세 조회
export async function getContract(id, token) {
  return await mainCaller(`admin/api/v1/contracts/${id}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 16. 계약 PDF 다운로드 (바이너리)
export async function getContractPdf(id, token) {
  const res = await axios({
    method: 'GET',
    url: `${getMainUrl()}/admin/api/v1/contracts/${id}/pdf/download`,
    headers: { Authorization: `Bearer ${token || getToken()}` },
    responseType: 'blob',
    withCredentials: false,
  });
  return res.data;
}

// 17. 영수증 다운로드 (바이너리 — PDF/이미지 자동 판별)
export async function getReceiptDownload(receiptId, token) {
  const res = await axios({
    method: 'GET',
    url: `${getMainUrl()}/admin/api/v1/contracts/receipts/${receiptId}/download`,
    headers: { Authorization: `Bearer ${token || getToken()}` },
    responseType: 'blob',
    withCredentials: false,
  });
  return res;
}

// 18. 계약 PDF URL 조회 (iframe용)
export async function getContractPdfUrl(id, token) {
  return await mainCaller(`admin/api/v1/contracts/${id}/pdf`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 18. 회원별 계약서 목록 조회 (manager — 회원상세 탭에서 사용)
export async function getMemberContracts(branchId, query, token) {
  return await mainCaller(`manager/api/v1/contracts/branch/${branchId}?${query}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 20. 회원별 계약서 목록 조회 (admin — 회원상세 계약서 탭)
export async function getMemberContractsByMemberId(memberId, query, token) {
  const url = query
    ? `admin/api/v1/contracts/members/${memberId}?${query}`
    : `admin/api/v1/contracts/members/${memberId}`;
  return await mainCaller(url, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 19. 계약 상세 조회 (manager — 회원상세에서 사용, receipts 포함)
export async function getMemberContractDetail(contractId, token) {
  return await mainCaller(`manager/api/v1/contracts/${contractId}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 19. 계약 카테고리 목록 조회
export async function getContractCategories(token) {
  return await mainCaller('admin/api/v1/contracts/categories', 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 19. 계약 상태 목록 조회
export async function getContractStatuses(token) {
  return await mainCaller('admin/api/v1/contracts/statuses', 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 21. 상품 기준 최신 환불/중지 계약 조회 (환불/중지 계약 작성은 계약앱에서만 하므로, CRM은
// 계약앱에서 이미 작성된 계약이 있는지만 조회한다. 없으면 null)
export async function getSourceContract(sourceProductId, category, token) {
  return await mainCaller(
    `admin/api/v1/contracts/source?sourceProductId=${sourceProductId}&category=${category}`,
    'GET',
    null,
    { Authorization: `Bearer ${token}` },
  );
}